import type { ProposalLogEntry } from '../types/types.TimesheetProposals';

/**
 * Normalize an entry/item id to a stable Map/Set key. On the Rails backend
 * entry ids are numbers (bigint) while the TIC log payload stringifies them; on
 * the legacy TIC backend they were UUID strings. Coercing every id to a string
 * gives one consistent key space so a numeric `entry.id` and a (possibly
 * stringified) `details.entry_id` reconcile regardless of source.
 */
const toEntryKey = (id: string | number): string => String(id);

// Fields whose change does NOT affect the AI-generated note text. An
// entry_updated log that only touches these is logged for audit but should
// not flip the note into a "stale, regenerate me" state.
const NON_CONTENT_FIELDS = new Set(['timely_label_ids', 'resource_id']);

// Minimal structural shape of the fields this helper reads off an entry. Kept
// id-type-agnostic (`string | number`) so it works for both legacy TIC UUID ids
// and Rails numeric (bigint) ids without coupling to either generated artifact.
type EditedEntryShape = { id: string | number; metadata?: unknown };

// Extract entry IDs that were edited by a user, based on proposal logs.
// This avoids relying on updated_at which may change due to system status updates (e.g. submit/revert).
// Excludes entries that were regenerated and haven't been edited since regeneration.
// Returns string keys (ids coerced via `String`) — callers comparing a raw
// numeric/Rails id must normalize with `String(entry.id)` before `.has(...)`.
export const getEditedEntryIdsFromLogs = (
  logs: ReadonlyArray<ProposalLogEntry> | undefined,
  entries?: ReadonlyArray<EditedEntryShape>
): Set<string> => {
  const edited = new Set<string>();
  if (!logs || logs.length === 0) return edited;

  const consideredActions = new Set<ProposalLogEntry['action']>([
    'entry_updated',
    'entry_added',
    'entry_merged',
    'item_updated',
    'item_assigned',
    'item_unassigned',
  ]);

  // Build map of entry_id -> last edit timestamp
  const lastEditByEntry = new Map<string, string>();

  for (const log of logs) {
    if (!consideredActions.has(log.action)) continue;
    // Only count explicit user edits
    if (!log.user_id) continue;

    const details = log.details || {};

    // entry_updated logs carry the changed field names; skip when the change
    // is purely categorisation (labels, linked work) — those don't make the
    // generated note stale.
    if (log.action === 'entry_updated') {
      const updatedFields = Array.isArray(details.updated_fields)
        ? (details.updated_fields as string[])
        : [];
      if (updatedFields.length > 0 && updatedFields.every(f => NON_CONTENT_FIELDS.has(f))) {
        continue;
      }
    }

    const maybeIds: Array<unknown> = [details.entry_id, details.moved_to_entry_id];

    for (const rawId of maybeIds) {
      // Rails serves numeric (bigint) entry ids; TIC served UUID strings. Accept
      // both — a `0` id is impossible for a real entry, so falsy values (null,
      // undefined, empty string) are correctly skipped.
      if ((typeof rawId !== 'string' && typeof rawId !== 'number') || !rawId) continue;
      const id = toEntryKey(rawId);
      edited.add(id);
      // Track the most recent edit for this entry
      const currentLast = lastEditByEntry.get(id);
      if (!currentLast || log.timestamp > currentLast) {
        lastEditByEntry.set(id, log.timestamp);
      }
    }
  }

  // Filter out entries that were regenerated and not edited afterwards
  if (entries) {
    for (const entry of entries) {
      const entryKey = toEntryKey(entry.id);
      if (!edited.has(entryKey)) continue;

      const metadata = entry.metadata as Record<string, unknown> | null | undefined;
      const noteLastRegeneratedAt = metadata?.['note_last_regenerated_at'];

      // If entry has regeneration timestamp and last edit was before it, exclude it
      if (typeof noteLastRegeneratedAt === 'string') {
        const lastEdit = lastEditByEntry.get(entryKey);
        if (!lastEdit || lastEdit < noteLastRegeneratedAt) {
          edited.delete(entryKey);
        }
      }
    }
  }

  return edited;
};
